import React, { useEffect, useRef, useState } from 'react';
import CallReadinessChecklist from './CallReadinessChecklist';

const SPEAKER_COLORS = [
  'var(--mppc-accent-secondary)',
  '#f59e0b',
  '#10b981',
  '#ec4899',
  '#8b5cf6',
];

function SpeakerLabel({ label, speakerIndex, color, onRename }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(label);
  const inputRef = useRef(null);

  useEffect(() => { setValue(label); }, [label]);
  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  const commit = () => {
    setEditing(false);
    const trimmed = value.trim();
    if (trimmed && trimmed !== label) onRename(speakerIndex, trimmed);
    else setValue(label);
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setValue(label); setEditing(false); } }}
        style={{
          fontSize: '11px', fontWeight: 600, color, background: 'rgba(255,255,255,0.06)',
          border: '1px solid rgba(255,255,255,0.15)', borderRadius: 4, padding: '1px 4px',
          outline: 'none', width: 120,
        }}
      />
    );
  }

  return (
    <span
      onClick={() => onRename && setEditing(true)}
      title={onRename ? 'Click to rename' : undefined}
      style={{
        fontSize: '11px', fontWeight: 600, color, marginBottom: '2px',
        cursor: onRename ? 'pointer' : 'default',
        borderBottom: onRename ? '1px dashed rgba(255,255,255,0.2)' : 'none',
      }}
    >
      {label}
    </span>
  );
}

export default function TranscriptView({
  transcripts,
  wsConnected,
  deviceReady,
  playbookName,
  creditsOk,
  inCall,
  roleLabels,
  hideTranscript,
  speakerMap,
  onRenameSpeaker,
}) {
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [transcripts]);

  if (hideTranscript) {
    return (
      <div className="transcript-compliance-notice">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        </svg>
        <div className="transcript-compliance-title">Compliance Mode</div>
        <div className="transcript-compliance-desc">
          Transcript is not displayed or saved. AI coaching, intel, and research remain active.
        </div>
      </div>
    );
  }

  if (transcripts.length === 0) {
    return (
      <div className="transcript-idle">
        <div className="transcript-idle-aurora" />
        <CallReadinessChecklist
          wsConnected={wsConnected}
          deviceReady={deviceReady}
          playbookName={playbookName}
          creditsOk={creditsOk}
          inCall={inCall}
        />
      </div>
    );
  }

  return (
    <div
      style={{
        flex: 1,
        overflowY: 'auto',
        padding: '12px',
      }}
    >
      {transcripts.map((entry, i) => {
        const isAgent = entry.role === 'agent';
        const hasSpeaker = !isAgent && entry.speakerIndex != null;
        const speakerLabel = hasSpeaker
          ? (speakerMap?.[entry.speakerIndex] || entry.speakerLabel || `Speaker ${entry.speakerIndex}`)
          : null;
        const speakerColor = hasSpeaker
          ? SPEAKER_COLORS[entry.speakerIndex % SPEAKER_COLORS.length]
          : null;

        return (
          <div
            key={i}
            style={{
              marginBottom: '8px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: isAgent ? 'flex-end' : 'flex-start',
            }}
          >
            {isAgent ? (
              <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--mppc-accent-light)', marginBottom: '2px' }}>
                {roleLabels?.agent || 'Agent'}
              </div>
            ) : hasSpeaker ? (
              <SpeakerLabel
                label={speakerLabel}
                speakerIndex={entry.speakerIndex}
                color={speakerColor}
                onRename={onRenameSpeaker}
              />
            ) : (
              <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--mppc-accent-secondary)', marginBottom: '2px' }}>
                {roleLabels?.customer || 'Customer'}
              </div>
            )}
            <div
              style={{
                background: isAgent ? 'rgba(99, 102, 241, 0.1)' : hasSpeaker ? `${speakerColor}11` : 'rgba(255, 255, 255, 0.04)',
                border: isAgent
                  ? '1px solid rgba(99, 102, 241, 0.15)'
                  : hasSpeaker ? `1px solid ${speakerColor}22` : '1px solid rgba(255, 255, 255, 0.06)',
                padding: '8px 12px',
                borderRadius: isAgent
                  ? '12px 12px 0 12px'
                  : '12px 12px 12px 0',
                maxWidth: '80%',
                fontSize: '14px',
                lineHeight: 1.4,
                color: 'var(--mppc-text-heading)',
              }}
            >
              {entry.text}
            </div>
            <div
              style={{
                fontSize: '10px',
                color: 'rgba(255, 255, 255, 0.2)',
                marginTop: '2px',
              }}
            >
              {new Date(entry.timestamp).toLocaleTimeString()}
            </div>
          </div>
        );
      })}
      <div ref={bottomRef} />
    </div>
  );
}
